import React, { useEffect, useState } from "react";
import axios from "../axios";
import { DATE_BADGE } from "../utils/dayType";

// ── コメント投稿支援 ──
//
// 投稿フォームの (曜日, 路線, 始発地, 時刻) で GET /api/comment-suggestions を
// 引き、候補があればボタンを光らせる。タップでモーダルを開き、選んだ候補を
// コメント欄へ入れる。
//
// 候補は過去 90 日の同じ曜日の投稿を多い順に 7 件。時刻表は見ない。
//
// **曜日は投稿日のダイヤ種別が既定**で、モーダルで選び直せる。特別日に
// 入れ忘れた年末年始のように、暦とダイヤがずれる日のため。選び直した曜日は
// 投稿日・路線・始発地・時刻を変えても保つ（同じ日の投稿を続けるため）。
// 投稿日が変わったら（モーダルを開き直したら）自動へ戻すのは呼び出し側。

export const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };

export function useCommentSuggestions({ enabled, routeId, startId, time, dayType, date }) {
  const [state, setState] = useState({ loading: false, items: [] });

  useEffect(() => {
    if (!enabled || !routeId || !startId || !/^\d{2}:\d{2}$/.test(time || "")) {
      setState({ loading: false, items: [] });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    // 時刻の入力中に 1 文字ごとに引かない
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get("/comment-suggestions", {
          params: { route_id: routeId, start_id: startId, time, day_type: dayType, date },
        });
        if (!cancelled) {
          setState({ loading: false, items: res.data.items || [] });
        }
      } catch {
        if (!cancelled) setState({ loading: false, items: [] });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, routeId, startId, time, dayType, date]);

  return state;
}

// コメント欄の横に置くボタン。路線・始発地・時刻が揃うまでは押せない。
// 揃っていれば候補が無くても押せる（曜日を選び直すとき）が、光るのは候補があるときだけ。
//
// 光っているかは色だけに頼らない。候補があるときは塗りつぶしに変え、
// 候補の数を添える（無いときは白地の枠だけで数も出さない）
export function CommentAssistButton({ ready, lit, count = 0, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!ready}
      aria-label="コメント候補"
      title={lit ? `コメント候補 ${count}件` : "コメント候補"}
      className={lit ? "comment-assist-lit" : undefined}
      style={{
        flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
        padding: "0 10px", borderRadius: "10px",
        border: lit ? "1.5px solid #d97706" : "1.5px solid #cbd5e1",
        background: lit ? "#fbbf24" : "#fff",
        color: lit ? "#78350f" : "#64748b",
        fontSize: "12px", fontWeight: "700",
        cursor: ready ? "pointer" : "not-allowed",
        opacity: ready ? 1 : 0.45,
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <style>{`
        @keyframes commentAssistGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(217,119,6,0.6); }
          50%      { box-shadow: 0 0 0 6px rgba(217,119,6,0); }
        }
        .comment-assist-lit { animation: commentAssistGlow 1.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .comment-assist-lit { animation: none; } }
      `}</style>
      候補
      {lit && (
        <span style={{
          minWidth: "16px", boxSizing: "border-box",
          padding: "0 5px", borderRadius: "99px",
          background: "#78350f", color: "#fef3c7",
          fontSize: "10.5px", lineHeight: "16px", textAlign: "center",
          fontVariantNumeric: "tabular-nums",
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

export function CommentAssistModal({
  isOpen, onClose, onPick, suggestions, dayType, autoDayType, onDayTypeChange,
  routeLabel, startLabel, time,
}) {
  if (!isOpen) return null;
  const { loading, items } = suggestions;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px", boxSizing: "border-box",
        backgroundColor: "rgba(2,12,8,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="コメント候補"
        style={{
          background: "#fff", borderRadius: "20px", padding: "18px",
          width: "100%", maxWidth: "380px", maxHeight: "80dvh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 48px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.08)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
          コメント候補
        </h3>
        <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px", lineHeight: 1.5 }}>
          {routeLabel} ／ {startLabel} {time}発
        </div>

        {/* 曜日。既定は投稿日のダイヤ種別 */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
          {["weekday", "sat", "holiday"].map((t) => {
            const on = dayType === t;
            const badge = DATE_BADGE[t];
            return (
              <button
                key={t}
                type="button"
                onClick={() => onDayTypeChange(t)}
                aria-pressed={on}
                style={{
                  flex: 1, padding: "7px 0", borderRadius: "10px",
                  border: `1.5px solid ${on ? badge.border : "#e2e8f0"}`,
                  background: on ? badge.bg : "#fff",
                  color: on ? badge.color : "#475569",
                  fontSize: "13px", fontWeight: "700", cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {DAY_TYPE_LABEL[t]}{t === autoDayType ? "（自動）" : ""}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "12px" }}>
          {dayType === autoDayType
            ? "投稿日のダイヤで選んでいます。年末年始などで違うときは選び直してください。"
            : `投稿日は${DAY_TYPE_LABEL[autoDayType]}ですが、${DAY_TYPE_LABEL[dayType]}ダイヤで探しています。`}
        </div>

        <div style={{ fontSize: "11px", fontWeight: "700", color: "#10b981", marginBottom: "6px" }}>
          過去90日の投稿より（多い順）
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: "13px", padding: "20px 0" }}>
              読み込み中...
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: "13px", padding: "20px 0", lineHeight: 1.6 }}>
              {`${DAY_TYPE_LABEL[dayType]}のこの便の投稿は過去90日にありません`}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {items.map((it) => (
                <button
                  key={it.comment}
                  type="button"
                  onClick={() => onPick(it.comment)}
                  style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    textAlign: "left", padding: "10px 12px", borderRadius: "10px",
                    border: "1px solid #d1fae5", background: "#f8fffe",
                    color: "#0f172a", fontSize: "14px", fontWeight: "600", cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  {/* 件数は左に細いバッジで。幅を揃えてコメントの頭を縦に並べる。
                      3 桁（「100件」）まで同じ幅に収まる最小幅にしてある。28px では
                      1 桁と 2 桁で幅が変わり、コメントの頭がずれた */}
                  <span style={{
                    flexShrink: 0, minWidth: "4em", boxSizing: "border-box",
                    padding: "1px 6px", borderRadius: "99px",
                    background: "#d1fae5", color: "#047857",
                    fontSize: "10.5px", fontWeight: "700", lineHeight: "16px",
                    textAlign: "center", fontVariantNumeric: "tabular-nums",
                  }}>
                    {it.count}件
                  </span>
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{it.comment}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: "12px", padding: "10px", borderRadius: "10px",
            border: "1.5px solid #e2e8f0", background: "#fff",
            color: "#64748b", cursor: "pointer", fontSize: "14px", fontWeight: "600",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          閉じる
        </button>
      </div>
    </div>
  );
}
